import { _ } from "libs";

import React, { useState, memo, forwardRef, useEffect, useRef } from "react";
import { auditTime, debounceTime, throttleTime } from "rxjs/operators";
import RxTextarea from "./rxTextarea";
import { Subject, Subscription } from "rxjs";
import EmojiPicker from "./emojiPicker";

type Props = {
	onSend: (message: string) => void;
	onTyping?: Function;
	onTypingStop?: Function;
	conversationId?: string;
	defaultMessage?: string;
};

const initialState = (defaultMessage: string): { value: string; isEnabled: boolean } => {
	return { value: defaultMessage, isEnabled: defaultMessage.length > 0 };
};

const MessageEditor = ({ conversationId = null, onTyping, onTypingStop, onSend, defaultMessage = "" }: Props, ref) => {
	const [state, setState] = useState(initialState(defaultMessage));
	const emojiPicker = useRef<typeof EmojiPicker>(null as typeof EmojiPicker);

	useEffect(() => {
		clear();
	}, [conversationId]);

	const clear = () => {
		setState(initialState(defaultMessage));
	};

	const subscriptionFactory = (subject: Subject<string>): Subscription[] => {
		if (onTyping) {
			return [
				subject.pipe(auditTime(500), throttleTime(2000)).subscribe(e => {
					onTyping();
				}),
				subject.pipe(debounceTime(1000)).subscribe(e => {
					onTypingStop();
				}),
			];
		}
		return [];
	};

	const onChange = (value: string) => {
		setState({ isEnabled: value.length > 0, value });
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		//@ts-ignore
		emojiPicker.current.close();

		if (e.key === "Enter") {
			e.preventDefault();
			let value = state.value;
			if (e.ctrlKey || e.shiftKey) {
				if (value.length > 0) {
					value = value + "\n";
					setState({ isEnabled: value.length > 0, value });
				}
			} else {
				clear();
				onSend(value.trim());
			}
		}
	};

	const onSendClick = () => {
		const value = state.value;

		clear();
		onSend(value.trim());
	};

	const addEmoji = emoji => {
		ref.current.insertAtCursor(emoji.native);
	};

	return (
		<div className="messageEditor" data-is-enabled={state.isEnabled}>
			<EmojiPicker onSelect={addEmoji} ref={emojiPicker} />
			<RxTextarea
				subscriptionFactory={subscriptionFactory}
				value={state.value}
				onChange={onChange}
				inputProps={{
					onKeyDown: onKeyDown,
					placeholder: _("messageComposer.placeholder"),
				}}
				ref={ref}
			/>
			<div className="sendMessageButton" onClick={() => onSendClick()} />
		</div>
	);
};

export default memo(forwardRef(MessageEditor));
